import { useEffect, useRef, useState } from 'react';
import { api } from '@/api/client';
import type { Plan, UpgradePreview } from './types';
import { formatMoney } from './dateFormat';

/**
 * Upgrade confirmation modal. Opened from an "Upgrade now" plan card, it fetches
 * the prorated breakdown (credit for the current plan's unused days + the revised
 * amount due) and shows it before charging. "Pay now" hands off to the parent,
 * which opens Razorpay Checkout for that revised amount.
 *
 * Layered above the Settings modal (.billing-invoice-overlay z-index) and reuses
 * the .billing-invoice row styling for a consistent look with InvoiceModal.
 */

interface UpgradeModalProps {
  /** The plan being upgraded to, or null when closed. */
  plan: Plan | null;
  /** An applied coupon for this plan (validated on the card), or null. */
  coupon?: { code: string; discountAmount: number; finalAmount: number } | null;
  /** Confirm → open checkout. Runs synchronously inside the click so the
   *  browser doesn't block the popup window the parent opens. */
  onPayNow(plan: Plan): void;
  onClose(): void;
  /** A checkout/poll is already in flight elsewhere — disable Pay now. */
  busy: boolean;
}

export function UpgradeModal({ plan, coupon, onPayNow, onClose, busy }: UpgradeModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const [preview, setPreview] = useState<UpgradePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const open = plan != null;

  // Esc to close (don't also close the Settings modal underneath).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
    }
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [open, onClose]);

  // Move focus in on open, restore it on close.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    return () => previouslyFocused?.focus?.();
  }, [open]);

  // Fetch the prorated quote whenever a plan is selected.
  useEffect(() => {
    if (!plan) { setPreview(null); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setPreview(null);
    api
      .get<{ preview: UpgradePreview }>(`/api/v2/subscriptions/upgrade-preview?planId=${encodeURIComponent(plan.id)}`)
      .then((r) => { if (!cancelled) setPreview(r.preview); })
      .catch((e) => { if (!cancelled) setError((e as Error).message || 'Could not load pricing'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [plan]);

  if (!plan) return null;

  const currency = preview?.currency ?? plan.currency;
  const newPrice = preview?.newPrice ?? Number(plan.price);
  const credit = preview?.credit ?? 0;
  // Proration amount due (before any coupon), then the coupon discount on top.
  const baseDue = preview?.amountDue ?? newPrice;
  const couponDiscount = coupon ? Math.min(coupon.discountAmount, baseDue) : 0;
  const amountDue = coupon ? Math.max(0, Math.round((baseDue - couponDiscount) * 100) / 100) : baseDue;
  const isFree = amountDue <= 0;

  return (
    <div className="settings-overlay billing-invoice-overlay" onMouseDown={onClose}>
      <div
        className="settings-modal billing-invoice"
        role="dialog"
        aria-modal="true"
        aria-label={`Upgrade to ${plan.name}`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <header className="settings-modal__head">
          <h2 className="settings-modal__title">Upgrade to {plan.name}</h2>
          <button
            type="button"
            ref={closeRef}
            className="settings-modal__close"
            aria-label="Close"
            title="Close"
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="settings-body">
          {loading ? (
            <p className="settings-muted">Calculating your prorated amount…</p>
          ) : error ? (
            <div className="settings-error">{error}</div>
          ) : (
            <>
              <div className="billing-invoice__amount">{isFree ? 'FREE' : formatMoney(amountDue, currency)}</div>
              <dl className="billing-invoice__rows">
                <div className="billing-invoice__row">
                  <dt className="billing-invoice__label">{plan.name} plan price</dt>
                  <dd className="billing-invoice__value">{formatMoney(newPrice, currency)}</dd>
                </div>
                {credit > 0 && (
                  <div className="billing-invoice__row">
                    <dt className="billing-invoice__label">
                      Credit for your current plan
                      {preview ? ` (${preview.remainingDays} of ${preview.totalDays} days left)` : ''}
                    </dt>
                    <dd className="billing-invoice__value">−{formatMoney(credit, currency)}</dd>
                  </div>
                )}
                {coupon && couponDiscount > 0 && (
                  <div className="billing-invoice__row">
                    <dt className="billing-invoice__label">Coupon ({coupon.code})</dt>
                    <dd className="billing-invoice__value" style={{ color: '#16a34a' }}>−{formatMoney(couponDiscount, currency)}</dd>
                  </div>
                )}
                <div className="billing-invoice__row billing-invoice__row--total">
                  <dt className="billing-invoice__label">{isFree ? 'You pay today' : 'Amount due today'}</dt>
                  <dd className="billing-invoice__value">{isFree ? 'FREE' : formatMoney(amountDue, currency)}</dd>
                </div>
              </dl>
              <p className="billing-invoice__note">
                {isFree
                  ? `🎉 Your coupon ${coupon ? coupon.code + ' ' : ''}covers the full amount — nothing to pay. Your ${plan.name} plan activates instantly.`
                  : credit > 0
                    ? 'You only pay the difference — your current plan’s unused days are credited. Your renewal date stays the same.'
                    : 'Your plan changes as soon as payment completes.'}
              </p>
            </>
          )}
        </div>

        <div className="settings-body billing-upgrade__actions">
          <button
            type="button"
            className="billing-btn billing-btn--ghost"
            onClick={onClose}
            disabled={busy}
          >
            Cancel
          </button>
          <button
            type="button"
            className="billing-btn billing-btn--primary"
            onClick={() => onPayNow(plan)}
            disabled={loading || busy || error != null}
          >
            {busy ? 'Opening…' : isFree ? 'Get it free →' : `Pay ${formatMoney(amountDue, currency)} now`}
          </button>
        </div>
      </div>
    </div>
  );
}
